/**
 * Marketing Attribution and Email Campaign Tracking
 *
 * Handles multi-touch attribution for marketing campaigns including:
 * - Email campaign click tracking
 * - UTM parameter extraction
 * - Acquisition channel detection (Google Ads, social media, NASW, schools, etc.)
 * - Conversion attribution with time-to-conversion tracking
 *
 * Data is persisted in localStorage with TTL:
 * - Email attribution: 7 days (conversion window)
 * - General attribution: 90 days (long attribution window)
 *
 * @module emailAttribution
 */

import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { AcquisitionChannel, UTMParams } from '@/types/user';
import { setItemWithTTL, getItemWithTTL, TTL } from '@/utils/localStorageWithTTL';

/**
 * Email click attribution data
 */
interface EmailAttributionData {
  /** UTM source (should be 'email') */
  source: string;
  /** Email campaign identifier */
  campaign: string;
  /** Unix timestamp when email link was clicked */
  clickTimestamp: number;
}

/**
 * Comprehensive attribution data for a user visit
 */
interface AttributionData {
  /** Detected acquisition channel */
  acquisitionChannel: AcquisitionChannel;
  /** All UTM parameters from the URL */
  utmParams: UTMParams;
  /** HTTP referrer if available */
  referrer?: string;
  /** School/university identifier for partnership tracking */
  schoolId?: string;
  /** Association identifier (e.g., NASW chapter) */
  associationId?: string;
  /** Unix timestamp when attribution was detected */
  detectedAt: number;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
}

type ClickIds = Pick<AttributionData, 'gclid' | 'gbraid' | 'wbraid'>;

/** Google Ads click ids from the current URL; absent keys are omitted. */
const extractClickIds = (): ClickIds => {
  const p = new URLSearchParams(window.location.search);
  const out: ClickIds = {};
  for (const k of ['gclid', 'gbraid', 'wbraid'] as const) {
    const v = p.get(k);
    if (v) out[k] = v;
  }
  return out;
};

/**
 * Captures email attribution data from URL parameters
 *
 * Detects email campaign clicks and stores attribution for conversion tracking.
 * Automatically called on app initialization.
 *
 * Expected URL format: `?utm_source=email&utm_campaign=abandoned_quiz&email_click_time=1234567890`
 *
 * @example
 * ```typescript
 * // Called automatically in App.tsx on page load
 * captureEmailAttribution();
 * ```
 */
/**
 * Sanitizes a URL parameter value to prevent XSS attacks
 * Removes potentially malicious patterns while preserving legitimate values
 */
const sanitizeParam = (value: string | null): string | null => {
  if (!value) return null;

  // Remove script tags, javascript: protocol, and other dangerous patterns
  const sanitized = value
    .replace(/<script[^>]*>.*?<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '') // Remove event handlers like onclick=
    .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
    .replace(/[<>]/g, ''); // Remove angle brackets entirely

  // Limit length to prevent abuse
  return sanitized.substring(0, 500);
};

export const captureEmailAttribution = (): void => {
  const urlParams = new URLSearchParams(window.location.search);

  const emailSource = sanitizeParam(urlParams.get('utm_source'));
  const emailCampaign = sanitizeParam(urlParams.get('utm_campaign'));
  const emailClickTime = urlParams.get('email_click_time');

  // Only capture if this looks like an email click
  if (emailSource === 'email' && emailCampaign && emailClickTime) {
    // Validate and parse timestamp with fallback
    const clickTimestamp = parseInt(emailClickTime, 10);
    if (isNaN(clickTimestamp)) {
      console.warn('Invalid email_click_time parameter:', emailClickTime);
      return;
    }

    const attributionData: EmailAttributionData = {
      source: emailSource,
      campaign: emailCampaign,
      clickTimestamp
    };
    
    // Store in localStorage for conversion attribution (7 days TTL)
    setItemWithTTL('email_attribution', attributionData, TTL.ONE_WEEK);
    
    // Track the email click event
    logAnalyticsEvent(ANALYTICS_EVENTS.LINK_CLICK, {
      link_url: window.location.href,
      link_text: `email_${emailCampaign}`,
      email_source: emailSource,
      email_campaign: emailCampaign
    });
  }
};

/**
 * Tracks a conversion event and attributes it to an email campaign if applicable
 *
 * Checks for stored email attribution data and logs the conversion with time-to-conversion metrics.
 * Automatically cleans up attribution data after conversion.
 *
 * @param conversionAction - Type of conversion (e.g., 'signup', 'purchase', 'trial_start')
 *
 * @example
 * ```typescript
 * // When user completes signup
 * trackEmailConversion('signup_completed');
 * ```
 */
export const trackEmailConversion = (conversionAction: string): void => {
  const emailData = getItemWithTTL<EmailAttributionData>('email_attribution');

  if (emailData) {
    try {
      const timeFromEmailToConversion = Date.now() - emailData.clickTimestamp;
      
      logAnalyticsEvent(ANALYTICS_EVENTS.EMAIL_CONVERSION, {
        email_source: emailData.source,
        email_campaign: emailData.campaign,
        conversion_action: conversionAction,
        time_from_email_to_conversion: timeFromEmailToConversion,
        email_click_timestamp: emailData.clickTimestamp
      });
      
      // Clean up attribution data after conversion
      localStorage.removeItem('email_attribution');
    } catch (error) {
      console.error('Error parsing email attribution data:', error);
    }
  }
};

/**
 * Retrieves stored email attribution data without logging any events
 *
 * Useful for checking if user arrived via email campaign without triggering conversion tracking.
 *
 * @returns Email attribution data if available and not expired, null otherwise
 *
 * @example
 * ```typescript
 * const emailData = getEmailAttribution();
 * if (emailData) {
 *   console.log(`User came from campaign: ${emailData.campaign}`);
 * }
 * ```
 */
export const getEmailAttribution = (): EmailAttributionData | null => {
  return getItemWithTTL<EmailAttributionData>('email_attribution');
};

/**
 * Detects the acquisition channel based on URL parameters and HTTP referrer
 *
 * Uses a priority-based detection algorithm:
 * 1. Google Ads (gclid parameter or utm_source=google + utm_medium=cpc)
 * 2. Explicit source parameter
 * 3. UTM parameters (source/medium)
 * 4. Email campaigns (with school/NASW sub-detection)
 * 5. Referrer domain analysis
 * 6. Referral parameters
 * 7. Fallback to 'organic' or 'other'
 *
 * @returns Detected acquisition channel
 *
 * Supported channels:
 * - `google_ads` - Paid Google Ads traffic
 * - `school_outreach` - Educational institution partnerships
 * - `nasw` - National Association of Social Workers
 * - `social_media` - Facebook, Instagram, LinkedIn, Twitter/X, Reddit
 * - `referral` - User referrals
 * - `organic` - Direct or organic search
 * - `other` - Unclassified traffic with referrer
 *
 * @example
 * ```typescript
 * const channel = detectAcquisitionChannel();
 * // Returns 'google_ads' for ?gclid=abc123
 * // Returns 'social_media' for referrer facebook.com
 * // Returns 'school_outreach' for referrer university.edu
 * ```
 */
export const detectAcquisitionChannel = (): AcquisitionChannel => {
  const urlParams = new URLSearchParams(window.location.search);
  const referrer = document.referrer.toLowerCase();

  // Check for Google Ads campaign (gclid parameter) - highest priority
  if (urlParams.get('gclid')) return 'google_ads';

  // Check for explicit source parameter
  const source = urlParams.get('source');
  if (source === 'school') return 'school_outreach';
  if (source === 'nasw') return 'nasw';
  if (source === 'referral') return 'referral';
  
  // Check UTM parameters
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');

  // Google Ads
  if (utmSource === 'google' && utmMedium === 'cpc') return 'google_ads';

  // Organic search (Google without paid)
  if (utmSource === 'google' && utmMedium !== 'cpc') return 'organic';

  // Social media detection
  if (utmSource === 'facebook' || utmSource === 'instagram' || utmSource === 'twitter' || utmSource === 'linkedin') {
    return 'social_media';
  }
  if (utmMedium === 'social') return 'social_media';
  if (utmSource === 'email') {
    // Email campaigns are tracked separately but could be school/NASW emails
    const campaign = urlParams.get('utm_campaign');
    if (campaign?.includes('school') || campaign?.includes('faculty')) return 'school_outreach';
    if (campaign?.includes('nasw')) return 'nasw';
  }
  
  // Check referrer domains
  if (referrer.includes('facebook.com') || referrer.includes('fb.com')) return 'social_media';
  if (referrer.includes('reddit.com')) return 'social_media';
  if (referrer.includes('instagram.com')) return 'social_media';
  if (referrer.includes('linkedin.com')) return 'social_media';
  if (referrer.includes('twitter.com') || referrer.includes('x.com')) return 'social_media';
  if (referrer.includes('socialworkers.org') || referrer.includes('nasw.org')) return 'nasw';
  if (referrer.includes('.edu')) return 'school_outreach';

  // Check for referral link patterns
  if (urlParams.get('ref') || urlParams.get('referral') || utmMedium === 'referral') return 'referral';

  // Default based on whether there's a referrer
  if (referrer) return 'other';
  return 'organic';
};

/**
 * Extracts all UTM parameters from the current URL
 *
 * Parses standard UTM parameters used for campaign tracking.
 *
 * @returns Object containing UTM parameters (undefined for missing params)
 *
 * @example
 * ```typescript
 * // URL: ?utm_source=google&utm_campaign=summer2025&utm_term=lcsw
 * const utmParams = extractUTMParams();
 * // { utm_source: 'google', utm_campaign: 'summer2025', utm_term: 'lcsw', ... }
 * ```
 */
export const extractUTMParams = (): UTMParams => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source') || undefined,
    utm_medium: urlParams.get('utm_medium') || undefined,
    utm_campaign: urlParams.get('utm_campaign') || undefined,
    utm_content: urlParams.get('utm_content') || undefined,
    utm_term: urlParams.get('utm_term') || undefined,
  };
};

/**
 * Captures and stores comprehensive attribution data for the current page visit
 *
 * Detects acquisition channel, extracts UTM parameters, captures referrer,
 * and stores partnership IDs (school/association). Data is persisted for 90 days.
 *
 * Should be called once on initial page load. Automatically called in App.tsx.
 *
 * @returns Complete attribution data object
 *
 * @example
 * ```typescript
 * // Called automatically on app initialization
 * const attribution = captureAttribution();
 * // Later use during signup:
 * const data = getAttributionData();
 * saveToFirestore(userId, { attributionData: data });
 * ```
 */
export const captureAttribution = (): AttributionData => {
  const acquisitionChannel = detectAcquisitionChannel();
  const utmParams = extractUTMParams();
  const urlParams = new URLSearchParams(window.location.search);
  
  const attributionData: AttributionData = {
    acquisitionChannel,
    utmParams,
    referrer: document.referrer || undefined,
    schoolId: urlParams.get('school_id') || undefined,
    associationId: urlParams.get('association_id') || undefined,
    detectedAt: Date.now(),
    ...extractClickIds(),
  };
  
  // Store in localStorage for use during signup (90 days TTL for longer attribution window)
  setItemWithTTL('attribution_data', attributionData, TTL.THREE_MONTHS);
  
  // Track attribution event
  logAnalyticsEvent(ANALYTICS_EVENTS.PAGE_VIEWED, {
    page_name: window.location.pathname,
    is_authenticated: false,
    timestamp: new Date().toISOString(),
    acquisition_channel: acquisitionChannel,
    utm_source: utmParams.utm_source,
    utm_campaign: utmParams.utm_campaign,
    referrer: attributionData.referrer,
  });
  
  return attributionData;
};

/**
 * Retrieves stored attribution data
 *
 * Returns the attribution data captured on initial page visit, if still valid (< 90 days old).
 * Used during signup to attach marketing attribution to user profile.
 *
 * @returns Attribution data if available and not expired, null otherwise
 *
 * @example
 * ```typescript
 * // During user signup
 * const attribution = getAttributionData();
 * if (attribution) {
 *   await createUser({
 *     ...userData,
 *     acquisitionChannel: attribution.acquisitionChannel,
 *     utmParams: attribution.utmParams
 *   });
 * }
 * ```
 */
export const getAttributionData = (): AttributionData | null => {
  return getItemWithTTL<AttributionData>('attribution_data');
};

/**
 * Flat attribution tags passed into Stripe Checkout so the backend can stamp
 * them onto the Subscription and attribute revenue. Sent identically by web AND
 * (later) the iOS app, so the backend contract is platform-agnostic.
 */
export interface CheckoutAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  landing_path?: string;
}

/**
 * Capture attribution ONLY when the current URL actually carries utm_* params or
 * a `school_id`. Unlike captureAttribution(), this never overwrites a prior
 * campaign on a plain page load — preserving correct last-touch attribution as
 * the user navigates. Call on app load alongside captureEmailAttribution().
 *
 * `school_id` is the partnership param captureAttribution() already reads, now
 * also carried by the /programs CTAs. This is the function that actually runs on
 * load (App.tsx), so without it here the code was dropped before signup. An id
 * with no UTM beside it rides along with whatever campaign is already stored
 * rather than resetting it, so the school survives without eating attribution.
 */
export const captureAttributionIfPresent = (): void => {
  const utm = extractUTMParams();
  const schoolId = sanitizeParam(new URLSearchParams(window.location.search).get('school_id')) || undefined;
  const clickIds = extractClickIds();
  const hasUtm = Boolean(utm.utm_source || utm.utm_campaign || utm.utm_medium);
  // Google Ads auto-tagging lands with a gclid and no utm_* at all; it joins the stored campaign.
  const hasClickId = Object.keys(clickIds).length > 0;
  if (!hasUtm && !hasClickId && !schoolId) return;
  const previous = hasUtm ? null : getAttributionData();
  setItemWithTTL(
    'attribution_data',
    {
      ...(previous ?? {
        acquisitionChannel: detectAcquisitionChannel(),
        utmParams: utm,
        referrer: document.referrer || undefined,
        detectedAt: Date.now(),
        landingPath: window.location.pathname,
        ...clickIds,
      }),
      ...(previous ? clickIds : {}),
      ...(schoolId ? { schoolId } : {}),
    } as AttributionData & { landingPath?: string },
    TTL.THREE_MONTHS
  );
};

/**
 * The flat UTM payload for the checkout call, or undefined if no attribution is
 * stored. Reads the last-touch attribution captured by captureAttributionIfPresent().
 */
export const getCheckoutAttribution = (): CheckoutAttribution | undefined => {
  const data = getAttributionData() as (AttributionData & { landingPath?: string }) | null;
  const utm = data?.utmParams;
  if (!utm) return undefined;
  const out: CheckoutAttribution = {};
  if (utm.utm_source) out.utm_source = utm.utm_source;
  if (utm.utm_medium) out.utm_medium = utm.utm_medium;
  if (utm.utm_campaign) out.utm_campaign = utm.utm_campaign;
  if (utm.utm_content) out.utm_content = utm.utm_content;
  if (utm.utm_term) out.utm_term = utm.utm_term;
  if (data?.landingPath) out.landing_path = data.landingPath;
  return Object.keys(out).length ? out : undefined;
};

/**
 * Flat, camelCase acquisition attribution to stamp on a captured lead. Mirrors
 * the lead-doc field names the backend persists.
 */
export interface LeadAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  landingPath?: string;
  referrer?: string;
}

/**
 * The acquisition attribution for a lead being captured, or undefined when the
 * visitor carries no channel signal (organic / direct — never fabricated).
 *
 * PREFERS the stored 90-day acquisition record (the channel that ORIGINALLY
 * brought the visitor) over the current URL's UTMs, so a lead is attributed to
 * the Google/Reddit/Meta/Substack touch that acquired them — not the last
 * internal click that happened to land them on the email gate. Falls back to the
 * current URL only when nothing is stored (e.g. a first-visit landing straight
 * on the gate before the on-load capture ran).
 */
export const getLeadAttribution = (): LeadAttribution | undefined => {
  const stored = getAttributionData() as (AttributionData & { landingPath?: string }) | null;
  const storedUtm = stored?.utmParams;
  const hasStored = Boolean(
    (storedUtm && (storedUtm.utm_source || storedUtm.utm_medium || storedUtm.utm_campaign)) ||
      stored?.gclid || stored?.gbraid || stored?.wbraid
  );
  const utm = hasStored ? storedUtm ?? {} : extractUTMParams();
  const clickIds: ClickIds = hasStored ? stored! : extractClickIds();

  const out: LeadAttribution = {};
  if (utm.utm_source) out.utmSource = utm.utm_source;
  if (utm.utm_medium) out.utmMedium = utm.utm_medium;
  if (utm.utm_campaign) out.utmCampaign = utm.utm_campaign;
  if (utm.utm_term) out.utmTerm = utm.utm_term;
  if (utm.utm_content) out.utmContent = utm.utm_content;
  if (clickIds.gclid) out.gclid = clickIds.gclid;
  if (clickIds.gbraid) out.gbraid = clickIds.gbraid;
  if (clickIds.wbraid) out.wbraid = clickIds.wbraid;
  const landingPath = hasStored ? stored?.landingPath : window.location.pathname;
  if (landingPath) out.landingPath = landingPath;
  const referrer = stored?.referrer || document.referrer || undefined;
  if (referrer) out.referrer = referrer;

  // Only report attribution when there is an actual channel signal (a UTM or ad click id). A
  // bare referrer/landingPath alone is not a channel — omit rather than fabricate.
  return out.utmSource || out.utmMedium || out.utmCampaign || out.gclid || out.gbraid || out.wbraid ? out : undefined;
};

/**
 * Clears stored attribution data
 *
 * Useful after conversion to prevent re-attribution, or for testing purposes.
 *
 * @example
 * ```typescript
 * // After successful conversion
 * trackEmailConversion('signup');
 * clearAttributionData();
 * ```
 */
export const clearAttributionData = (): void => {
  localStorage.removeItem('attribution_data');
};